/**
 * Custom Voice Service for Balakrishna Voice
 * 
 * This service provides:
 * 1. Consistent voice across all announcements
 * 2. Support for custom voice (Balakrishna actor voice)
 * 3. Multiple implementation options:
 *    - ElevenLabs Voice Cloning (Best quality)
 *    - Google Cloud TTS with custom voice
 *    - Pre-recorded audio files (Best for actor voice)
 *    - Browser native with consistent settings
 */

class CustomVoiceService {
  constructor() {
    // Configuration
    this.voiceProvider = process.env.REACT_APP_VOICE_PROVIDER || 'browser'; // 'browser', 'elevenlabs', 'google', 'prerecorded'
    this.elevenLabsApiKey = process.env.REACT_APP_ELEVENLABS_API_KEY;
    this.elevenLabsVoiceId = process.env.REACT_APP_ELEVENLABS_VOICE_ID; // Custom Balakrishna voice ID
    this.googleTTSApiKey = process.env.REACT_APP_GOOGLE_TTS_API_KEY;
    
    // Audio cache for pre-recorded files
    this.audioCache = new Map();
    this.audioContext = null;
    
    // Consistent voice settings for browser native
    this.browserVoiceSettings = {
      rate: 0.9,         // Natural speed for clarity
      pitch: 1.0,        // Clear, natural pitch
      volume: 1.0,
      lang: 'en-IN',     // English (Indian accent for clarity)
      voiceName: null    // Will be set from available voices
    };
    
    // Queue system for announcements (prevents overlapping)
    this.announcementQueue = [];
    this.isCurrentlySpeaking = false;
    
    
    // Initialize audio context for pre-recorded files
    if (this.voiceProvider === 'prerecorded') {
      this.initializeAudioContext();
    }
    
    // Load and select best English voice for browser
    if (this.voiceProvider === 'browser') {
      this.selectBestEnglishVoice();
    }
  }

  /**
   * Initialize Web Audio API context
   */
  initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (error) {
      console.error('❌ Failed to initialize audio context:', error);
    }
  }

  /**
   * Select best English voice for browser (clear and decent)
   */
  selectBestEnglishVoice() {
    if (!window.speechSynthesis) return;

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      // Priority: Clear English voices (Indian English > British English > US English > Any English)
      let selectedVoice = null;
      
      // 1. Look for Indian English (clear pronunciation)
      const indianEnglishVoice = voices.find(v => 
        v.lang.includes('en-IN') && !v.name.toLowerCase().includes('female')
      );
      
      // 2. Look for British English (clear accent)
      const britishVoice = voices.find(v => 
        v.lang.includes('en-GB') && !v.name.toLowerCase().includes('female')
      );
      
      // 3. Look for US English
      const usVoice = voices.find(v => 
        v.lang.includes('en-US') && !v.name.toLowerCase().includes('female')
      );
      
      // 4. Any English voice
      const anyEnglishVoice = voices.find(v => 
        v.lang.startsWith('en') && !v.name.toLowerCase().includes('female')
      );
      
      // 5. Fallback to any voice
      selectedVoice = indianEnglishVoice || britishVoice || usVoice || anyEnglishVoice || voices[0];
      
      if (selectedVoice) {
        this.browserVoiceSettings.voiceName = selectedVoice.name;
      }
    };

    // Load voices
    if (window.speechSynthesis.getVoices().length > 0) {
      loadVoices();
    } else {
      window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    }
  }

  /**
   * Main speak method - routes to appropriate provider
   * @param {string} text - Text to speak
   * @param {Object} options - Additional options
   */
  async speak(text, options = {}) {
    
    // Add to queue
    return new Promise((resolve, reject) => {
      this.announcementQueue.push({ text, options, resolve, reject });
      this.processQueue();
    });
  }

  /**
   * Process announcement queue (prevents overlapping speech)
   */
  async processQueue() {
    if (this.isCurrentlySpeaking || this.announcementQueue.length === 0) {
      return;
    }

    this.isCurrentlySpeaking = true;
    const { text, options, resolve, reject } = this.announcementQueue.shift();

    try {
      switch (this.voiceProvider) {
        case 'elevenlabs':
          await this.speakWithElevenLabs(text, options);
          break;
        case 'google':
          await this.speakWithGoogleTTS(text, options);
          break;
        case 'prerecorded':
          await this.speakWithPrerecorded(text, options);
          break;
        default:
          await this.speakWithBrowser(text, options);
      }
      resolve();
    } catch (error) {
      console.error('❌ Speech error:', error);
      reject(error);
    } finally {
      this.isCurrentlySpeaking = false;
      // Process next in queue
      if (this.announcementQueue.length > 0) {
        setTimeout(() => this.processQueue(), 500);
      }
    }
  }

  /**
   * Speak with ElevenLabs (Best for voice cloning - Balakrishna voice)
   * Steps to get Balakrishna voice:
   * 1. Go to elevenlabs.io
   * 2. Upload 5-10 audio samples of Balakrishna's voice
   * 3. Create voice clone
   * 4. Get voice ID and set in .env as REACT_APP_ELEVENLABS_VOICE_ID
   */
  async speakWithElevenLabs(text, options = {}) {
    if (!this.elevenLabsApiKey) {
      console.warn('⚠️ ElevenLabs API key not configured, falling back to browser');
      return this.speakWithBrowser(text, options);
    }

    if (!this.elevenLabsVoiceId) {
      console.warn('⚠️ ElevenLabs voice ID not configured, falling back to browser');
      return this.speakWithBrowser(text, options);
    }

    try {
      const response = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${this.elevenLabsVoiceId}`,
        {
          method: 'POST',
          headers: {
            'Accept': 'audio/mpeg',
            'Content-Type': 'application/json',
            'xi-api-key': this.elevenLabsApiKey
          },
          body: JSON.stringify({
            text: text,
            model_id: 'eleven_multilingual_v2', // Supports Telugu
            voice_settings: {
              stability: 0.5,
              similarity_boost: 0.75,
              style: 0.5,
              use_speaker_boost: true
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`ElevenLabs API error: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      return new Promise((resolve, reject) => {
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        audio.onerror = reject;
        audio.play();
      });
    } catch (error) {
      console.error('❌ ElevenLabs error:', error);
      return this.speakWithBrowser(text, options);
    }
  }

  /**
   * Speak with Google Cloud TTS
   */
  async speakWithGoogleTTS(text, options = {}) {
    if (!this.googleTTSApiKey) {
      console.warn('⚠️ Google TTS API key not configured, falling back to browser');
      return this.speakWithBrowser(text, options);
    }

    try {
      const response = await fetch(
        `https://texttospeech.googleapis.com/v1/text:synthesize?key=${this.googleTTSApiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            input: { text },
            voice: {
              languageCode: 'en-IN', // English (Indian)
              name: 'en-IN-Standard-B', // Male voice
              ssmlGender: 'MALE'
            },
            audioConfig: {
              audioEncoding: 'MP3',
              speakingRate: 0.95, // Natural speed
              pitch: 0.0,         // Natural pitch
              volumeGainDb: 0.0
            }
          })
        }
      );

      const data = await response.json();
      
      if (!data.audioContent) {
        throw new Error('No audio content received');
      }

      // Decode base64 audio
      const audioBlob = this.base64ToBlob(data.audioContent, 'audio/mp3');
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      return new Promise((resolve, reject) => {
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        audio.onerror = reject;
        audio.play();
      });
    } catch (error) {
      console.error('❌ Google TTS error:', error);
      return this.speakWithBrowser(text, options);
    }
  }

  /**
   * Speak with pre-recorded audio files (Best for actual Balakrishna voice)
   * 
   * How to use:
   * 1. Get Balakrishna to record these phrases:
   *    - "New order from table [1-100]"
   *    - "Please confirm payment for table [1-100]. Amount rupees [amount]"
   *    - Common menu items
   * 2. Save as MP3 files in: backend/public/audio/balakrishna/
   * 3. Name files like: table_1.mp3, table_2.mp3, payment_confirm.mp3, etc.
   * 4. This function will stitch them together
   */
  async speakWithPrerecorded(text, options = {}) {
    try {
      // Parse text and find matching pre-recorded files
      const audioFiles = this.mapTextToAudioFiles(text);
      
      if (audioFiles.length === 0) {
        console.warn('⚠️ No pre-recorded audio found, falling back to browser');
        return this.speakWithBrowser(text, options);
      }

      // Play audio files in sequence
      for (const audioFile of audioFiles) {
        await this.playAudioFile(audioFile);
      }
    } catch (error) {
      console.error('❌ Pre-recorded audio error:', error);
      return this.speakWithBrowser(text, options);
    }
  }

  /**
   * Map text to pre-recorded audio file paths
   */
  mapTextToAudioFiles(text) {
    const audioFiles = [];
    const baseUrl = process.env.REACT_APP_MAIN_QRINV_URL || 'http://localhost:5000';
    const audioPath = '/audio/balakrishna';
    
    // Example mapping logic
    // "New order from table 5. 2 Chicken Biryani. 1 Coke."
    
    // Extract table number
    const tableMatch = text.match(/table\s+(\d+)/i);
    if (tableMatch) {
      const tableNum = tableMatch[1];
      audioFiles.push(`${baseUrl}${audioPath}/new_order_table_${tableNum}.mp3`);
    }
    
    // Extract menu items
    const itemMatch = text.match(/(\d+)\s+([^.]+)/g);
    if (itemMatch) {
      itemMatch.forEach(item => {
        const [qty, name] = item.split(/\s+(.+)/);
        const sanitizedName = name.toLowerCase().replace(/\s+/g, '_');
        audioFiles.push(`${baseUrl}${audioPath}/item_${qty}_${sanitizedName}.mp3`);
      });
    }
    
    // Extract payment amount
    const amountMatch = text.match(/rupees\s+(\d+)/i);
    if (amountMatch) {
      const amount = amountMatch[1];
      audioFiles.push(`${baseUrl}${audioPath}/payment_amount_${amount}.mp3`);
    }
    
    return audioFiles;
  }

  /**
   * Play single audio file
   */
  async playAudioFile(audioUrl) {
    // Check cache first
    if (this.audioCache.has(audioUrl)) {
      const audio = this.audioCache.get(audioUrl).cloneNode();
      return new Promise((resolve) => {
        audio.onended = resolve;
        audio.play();
      });
    }

    // Load and cache
    const audio = new Audio(audioUrl);
    this.audioCache.set(audioUrl, audio);
    
    return new Promise((resolve, reject) => {
      audio.onended = resolve;
      audio.onerror = () => {
        console.warn(`⚠️ Failed to load audio: ${audioUrl}`);
        reject(new Error(`Audio not found: ${audioUrl}`));
      };
      audio.play().catch(reject);
    });
  }

  /**
   * Speak with browser (consistent settings for Balakrishna-style voice)
   */
  async speakWithBrowser(text, options = {}) {
    return new Promise((resolve, reject) => {
      if (!window.speechSynthesis) {
        console.warn('⚠️ speechSynthesis not supported');
        resolve(); // Don't reject, just resolve silently
        return;
      }

      // Check if speech synthesis is allowed (user gesture requirement)
      // In production with autoplay policies, we need user interaction first
      try {
        window.speechSynthesis.cancel();
      } catch (err) {
        console.warn('⚠️ Cannot cancel speech synthesis:', err.message);
      }

      const utterance = new SpeechSynthesisUtterance(text);
      
      // Use consistent settings (options override defaults)
      utterance.rate = options.rate !== undefined ? options.rate : this.browserVoiceSettings.rate;
      utterance.pitch = options.pitch !== undefined ? options.pitch : this.browserVoiceSettings.pitch;
      utterance.volume = options.volume !== undefined ? options.volume : this.browserVoiceSettings.volume;
      utterance.lang = options.lang || this.browserVoiceSettings.lang;

      // Select voice - prioritize options.voiceName over default
      const voiceNameToUse = options.voiceName || this.browserVoiceSettings.voiceName;
      const voices = window.speechSynthesis.getVoices();
      
      if (voices.length > 0 && voiceNameToUse) {
        const voice = voices.find(v => v.name === voiceNameToUse);
        if (voice) {
          utterance.voice = voice;
        } else {
          console.warn('⚠️ Voice not found:', voiceNameToUse, '- Using default');
        }
      }

      utterance.onend = () => {
        resolve();
      };
      
      utterance.onerror = (event) => {
        // Handle 'not-allowed' error gracefully (common in production)
        if (event.error === 'not-allowed') {
          console.warn('⚠️ Speech not allowed - user interaction required. Silently continuing.');
          resolve(); // Don't reject, just resolve
        } else {
          console.error('❌ Speech error:', event.error);
          resolve(); // Still resolve to prevent breaking the app
        }
      };

      try {
        window.speechSynthesis.speak(utterance);
      } catch (err) {
        console.warn('⚠️ Cannot speak:', err.message);
        resolve(); // Resolve gracefully
      }
    });
  }

  /**
   * Utility: Convert base64 to Blob
   */
  base64ToBlob(base64, mimeType) {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mimeType });
  }

  /**
   * Stop all announcements
   */
  stop() {
    this.announcementQueue = [];
    this.isCurrentlySpeaking = false;
    
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
  }

  /**
   * Get available voices for configuration
   */
  getAvailableVoices() {
    if (!window.speechSynthesis) return [];
    return window.speechSynthesis.getVoices();
  }

  /**
   * Change voice settings
   */
  updateVoiceSettings(settings) {
    this.browserVoiceSettings = {
      ...this.browserVoiceSettings,
      ...settings
    };
  }
}

// Create singleton instance
const customVoiceService = new CustomVoiceService();

export default customVoiceService;
